import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useAppContext } from "@components/Provider";
import styles from "./ContentHeader.module.css";
import { toTitleCaseAdvanced } from "../../helpers/Helper";

const ContentHeader = ({
  slug,
  isOpen,
  setIsOpen,
  isMobile,
  parentClasses = {
    itemClass: "nav-item",
    showClass: "show",
    dToggleClass: "dropdown-toggle",
    subMenuClass: "sub-menu",
  },
}) => {
  const { data: contextData } = useAppContext();

  // Custom click handler for server-side navigation
  const handleServerSideClick = (e, url, isExternal) => {
    if (isExternal) {
      return; // Let external links work normally
    }

    e.preventDefault(); // Prevent default Link behavior
    setIsOpen && setIsOpen(false); // Close menu if needed

    // Force server-side navigation by using window.location
    window.location.href = url;
  };

  let navItems = contextData?.SiteSettings?.site_header?.nav_items || [];
  const contentMenu = navItems;
  const [isDDOpen, setIsDDOpen] = React.useState(-1);
  const [childrenOpen, setChildrenOpen] = React.useState({});

  const ddToggle = (index) => {
    if (isDDOpen === index) {
      setIsDDOpen(-1);
    } else {
      setIsDDOpen(index);
    }
  };

  const handleChildrenHover = (parentIndex, childIndex, isHovering) => {
    if (isHovering) {
      setChildrenOpen((prev) => ({
        ...prev,
        [`${parentIndex}-${childIndex}`]: true,
      }));
    } else {
      setChildrenOpen((prev) => ({
        ...prev,
        [`${parentIndex}-${childIndex}`]: false,
      }));
    }
  };

  const ChevronIcon = ({ open }) => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles["chevron-icon"]}
      style={{
        transition: "transform 0.2s",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <path
        d="M6.5 9L11 13.5L15.5 9"
        stroke="#222"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const renderHeaderLinks = () => {
    return contentMenu?.map((link, key) => {
      const linkText = link.text.toLowerCase();
      const currentSlug = slug?.toString().replace(/-/g, " ");
      let updatedClassName = parentClasses.itemClass || "nav-item";
      if (isOpen) {
        updatedClassName = `${parentClasses.showClass || "show"} ${updatedClassName}`;
      }
      if (
        currentSlug &&
        currentSlug.split("?")[0].split("/")[1].toLowerCase() !== "search" &&
        currentSlug.split("?")[0].split("/").pop().toLowerCase() === linkText
      ) {
        updatedClassName += " dpactive";
      }

      const hasSubMenu = link?.subMenus && link?.subMenus.length > 0;

      const renderSubMenu = (subMenus) => {
        if (!subMenus || subMenus.length === 0) return null;

        // Debug: Log submenus with children
        const subMenusWithChildren = subMenus.filter(
          (item) => item?.children && item?.children.length > 0
        );
        if (subMenusWithChildren.length > 0) {
          console.log("Submenus with children:", subMenusWithChildren);
        }

        return (
          <ul
            className={
              (isDDOpen === key ? `${parentClasses.showClass || "show"} ` : "") +
              `${styles.subMenu} ${styles["dropdown-submenu"]} ${parentClasses.subMenuClass || "sub-menu"}`
            }
            style={{ display: isDDOpen === key ? "flex" : "none" }}
          >
            {isMobile ? (
              <li
                className={`sub-item ${styles.hideSubMenu} item-dd`}
                onClick={() => setIsOpen && setIsOpen(false)}
              >
                <Link
                  href={link.url}
                  onClick={(e) => handleServerSideClick(e, link.url, link.external)}
                >
                  ALL {link.text}
                </Link>
              </li>
            ) : null}
            {subMenus.map((item, index) => {
              const hasChildren = item?.children && item?.children.length > 0;
              return (
                <li
                  className={`sub-item item-dd ${hasChildren ? "has-children" : ""}`}
                  key={`submenu-${index}`}
                  onClick={() => !hasChildren && setIsOpen && setIsOpen(false)}
                  onMouseEnter={() => hasChildren && handleChildrenHover(key, index, true)}
                  onMouseLeave={() => hasChildren && handleChildrenHover(key, index, false)}
                  style={{ position: hasChildren ? "relative" : "static" }}
                >
                  {item?.external ? (
                    <a href={item.url} rel="noopener noreferrer">
                      {item?.imageUrl ? (
                        <Image
                          src={item.imageUrl}
                          alt={item?.text || ""}
                          width={20}
                          height={20}
                          className={styles.menuIcon}
                        />
                      ) : null}
                      <span>{item.text}</span>
                      {hasChildren ? <ChevronIcon open={false} /> : null}
                    </a>
                  ) : (
                    <Link
                      href={item.url}
                      onClick={(e) => handleServerSideClick(e, item.url, item.external)}
                    >
                      {item?.imageUrl ? (
                        <Image
                          src={item.imageUrl}
                          alt={item?.text || ""}
                          width={20}
                          height={20}
                          className={styles.menuIcon}
                        />
                      ) : null}
                      <span>{item.text}</span>
                      {hasChildren ? <ChevronIcon open={false} /> : null}
                    </Link>
                  )}

                  {/* Render children if they exist */}
                  {hasChildren ? (
                    <ul
                      className={`${styles.subMenu} ${styles["dropdown-submenu"]} ${styles.childrenSubmenu}`}
                      style={{
                        display: childrenOpen[`${key}-${index}`] ? "flex" : "none",
                      }}
                    >
                      {item.children.map((child, childIndex) => (
                        <li
                          className="sub-item item-dd child-item"
                          key={`child-${childIndex}`}
                          onClick={() => setIsOpen && setIsOpen(false)}
                        >
                          {child?.external ? (
                            <a href={child.url} rel="noopener noreferrer">
                              {child?.imageUrl ? (
                                <Image
                                  src={child.imageUrl}
                                  alt={child?.text || ""}
                                  width={20}
                                  height={20}
                                  className={styles.menuIcon}
                                />
                              ) : null}
                              <span>{child.text}</span>
                            </a>
                          ) : (
                            <Link
                              href={child.url}
                              onClick={(e) => handleServerSideClick(e, child.url, child.external)}
                            >
                              {child?.imageUrl ? (
                                <Image
                                  src={child.imageUrl}
                                  alt={child?.text || ""}
                                  width={20}
                                  height={20}
                                  className={styles.menuIcon}
                                />
                              ) : null}
                              <span>{child.text}</span>
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        );
      };

      const renderLink = (isOpenDropdown) => {
        const hasSubMenu = link?.subMenus && link?.subMenus.length > 0;
        if (hasSubMenu) {
          // For items with submenus, make the text clickable but keep dropdown functionality
          return (
            <div className={styles.dropdownLinkContainer}>
              {link.url ? (
                <Link
                  href={link.url === "/search" ? "/search" : link.url.toString().toLowerCase()}
                  className={styles.parentLink}
                  onClick={(e) => handleServerSideClick(e, link.url, link.external)}
                >
                  {link?.imageUrl ? (
                    <Image
                      src={link.imageUrl}
                      alt={link?.text || ""}
                      width={20}
                      height={20}
                      className={styles.menuIcon}
                    />
                  ) : null}
                  <span>{toTitleCaseAdvanced(link.text?.toString())}</span>
                </Link>
              ) : (
                <span className={styles.parentLink}>
                  {link?.imageUrl ? (
                    <Image
                      src={link.imageUrl}
                      alt={link?.text || ""}
                      width={20}
                      height={20}
                      className={styles.menuIcon}
                    />
                  ) : null}
                  <span>{toTitleCaseAdvanced(link.text?.toString())}</span>
                </span>
              )}
              <span
                className={styles.dropdownToggle}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  ddToggle(key);
                }}
              >
                <ChevronIcon open={isOpenDropdown} />
              </span>
            </div>
          );
        }
        if (link.external) {
          return (
            <a href={link.url} target="_blank" rel="noopener noreferrer">
              {link?.imageUrl ? (
                <Image
                  src={link.imageUrl}
                  alt={link?.text || ""}
                  width={20}
                  height={20}
                  className={styles.menuIcon}
                />
              ) : null}
              <span>{link.text?.toString()}</span>
            </a>
          );
        }
        return (
          <Link
            href={link.url === "/search" ? "/search" : link.url.toString().toLowerCase()}
            onClick={(e) => handleServerSideClick(e, link.url, link.external)}
          >
            {link?.imageUrl ? (
              <Image
                src={link.imageUrl}
                alt={link?.text || ""}
                width={20}
                height={20}
                className={styles.menuIcon}
              />
            ) : null}
            <span>{toTitleCaseAdvanced(link.text?.toString())}</span>
          </Link>
        );
      };

      return (
        <li
          className={
            updatedClassName +
            (hasSubMenu ? ` ${parentClasses.dToggleClass || "dropdown-toggle"}` : "")
          }
          key={key}
          onMouseEnter={() => hasSubMenu && setIsDDOpen(key)}
          onMouseLeave={() => hasSubMenu && setIsDDOpen(-1)}
          style={{ position: hasSubMenu ? "relative" : "static" }}
        >
          {renderLink(isDDOpen === key)}
          {hasSubMenu ? renderSubMenu(link.subMenus) : null}
        </li>
      );
    });
  };

  return <ul className={styles.menuList}>{renderHeaderLinks()}</ul>;
};

export default ContentHeader;
